import type { Cart, Order } from "@commercetools/platform-sdk";
import { useEventListener } from "@react-hookz/web";
import { isBrowser } from "./browser-utils";

/**
 * This type defines the events that can be sent to the browser.
 * The event name is the key, and the data is the value.
 * The data is the detailpayload of the event.
 * The event is a custom event that is dispatched to the window.
 */
type BrowserEvents = {
	AddToCart: {
		cart: Cart;
		items: {
			product: Queries.CommerceToolsProductFragment;
			variant: Queries.ProductVariantFragment;
		}[];
	};
	ViewVariant: {
		product: Queries.CommerceToolsProductFragment;
		variant: Queries.ProductVariantFragment;
	};
	OrderCreated: {
		order: Order;
	};
	InitiateCheckout: {
		cart: Cart;
	};
};

/**
 * This function sends a browser event to the window.
 *
 * @param event - The event name.
 * @param data - The data to send to the event.
 */
export const sendBrowserEvent = <EventName extends keyof BrowserEvents>(
	event: EventName,
	data: BrowserEvents[EventName],
) => {
	if (isBrowser()) {
		const customEvent = new CustomEvent<BrowserEvents[EventName]>(event, {
			detail: data,
		});

		globalThis.dispatchEvent(customEvent);
	}
};

/**
 * This function listens for a custom event on the window.
 * It is a wrapper around the useEventListener hook with the correct type.
 * useEventListener handles unlistening when the component unmounts.
 *
 * @param event - The event name.
 * @param callback - The callback function.
 */
export const useCustomEventListener = <EventName extends keyof BrowserEvents>(
	event: EventName,
	callback: (data: CustomEvent<BrowserEvents[EventName]>) => void,
	options?: AddEventListenerOptions,
) => {
	useEventListener(globalThis, event, callback, options);
};
